import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { sendEmail } from '../services/emailService';

interface TaxClient {
  id: string;
  name: string;
  email: string;
  phone?: string;
  slug: string;
  createdAt: Date;
  status: 'pending' | 'documents_requested' | 'documents_received' | 'in_progress' | 'completed';
  uploadCount: number;
  totalSize: number;
  files: UploadedFile[];
  lastActivity: Date;
  notes?: string;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
  uploaderEmail: string;
  storageKey: string;
  type?: string;
  category?: 'w2' | '1099' | 'bank_statements' | 'receipts' | 'other';
}

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  duration?: string;
  result?: string;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  category: 'initial_request' | 'reminder' | 'follow_up' | 'completion';
}

const TaxPreparerWorkflowDesigner: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'new-client' | 'client-management' | 'email-templates' | 'dashboard'>('new-client');
  const [clients, setClients] = useState<TaxClient[]>([]);
  const [selectedClient, setSelectedClient] = useState<TaxClient | null>(null);
  const [runningProcess, setRunningProcess] = useState(false);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);

  // New client form
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientNotes, setClientNotes] = useState('');

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([
    {
      id: 'initial',
      name: 'Initial Document Request',
      category: 'initial_request',
      subject: 'Tax Documents Required - {clientName}',
      body: `Dear {clientName},

Thank you for choosing our tax preparation services! To get started on your tax return, I'll need you to upload the following documents:

📄 **Required Documents:**
• W-2 forms from all employers
• 1099 forms (Interest, Dividends, Misc Income)
• Bank statements (checking, savings, investment accounts)
• Receipts for deductible expenses
• Last year's tax return (if not prepared by us)

🔗 **Upload Your Documents Here:**
{uploadLink}

📞 **Questions?** Call us at (555) 123-4567 or reply to this email.

We'll review your documents within 1-2 business days and contact you if we need anything additional.

Best regards,
[Your Name]
[Your Tax Preparation Business]`
    },
    {
      id: 'reminder',
      name: 'Document Reminder',
      category: 'reminder',
      subject: 'Reminder: Tax Documents Needed - {clientName}',
      body: `Hi {clientName},

This is a friendly reminder that we're still waiting for some documents to complete your tax return.

Current status: {status}
Documents received: {documentCount}

🔗 **Upload remaining documents here:**
{uploadLink}

If you have any questions or need help finding specific documents, please don't hesitate to reach out!

Best regards,
[Your Name]`
    },
    {
      id: 'completion',
      name: 'Tax Return Completed',
      category: 'completion',
      subject: 'Your Tax Return is Ready! - {clientName}',
      body: `Great news, {clientName}!

Your tax return has been completed and is ready for your review.

📊 **Return Summary:**
• Total documents processed: {documentCount}
• Filing status: [To be filled]
• Estimated refund/owe: [To be filled]

📋 **Next Steps:**
1. Review your return carefully
2. Sign and submit if everything looks correct
3. We'll handle the electronic filing

📞 **Schedule Review:** Call (555) 123-4567 to schedule your review appointment.

Thank you for trusting us with your tax preparation!

Best regards,
[Your Name]`
    }
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [emailPreview, setEmailPreview] = useState('');

  // Load saved data
  useEffect(() => {
    const savedClients = localStorage.getItem('tax_clients');
    if (savedClients) {
      setClients(JSON.parse(savedClients).map((c: any) => ({
        ...c,
        createdAt: new Date(c.createdAt),
        lastActivity: new Date(c.lastActivity)
      })));
    }
  }, []);

  // Save clients data
  useEffect(() => {
    try {
      localStorage.setItem('tax_clients', JSON.stringify(clients));
    } catch (e) {
      console.warn('Failed to save clients to localStorage:', e);
    }
  }, [clients]);

  // Generate slug from client name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Create new client workflow
  const runCreateClientWorkflow = async () => {
    if (!clientName || !clientEmail) {
      alert('Please enter client name and email');
      return;
    }

    setRunningProcess(true);
    const steps: WorkflowStep[] = [
      {
        id: 'validate',
        title: 'Validate Client Information',
        description: 'Check required fields and email format',
        status: 'active'
      },
      {
        id: 'generate-slug',
        title: 'Generate Client URL',
        description: 'Create unique upload URL for client',
        status: 'pending'
      },
      {
        id: 'create-client',
        title: 'Create Client Record',
        description: 'Store client information in system',
        status: 'pending'
      },
      {
        id: 'send-email',
        title: 'Send Document Request Email',
        description: 'Send initial email with upload link',
        status: 'pending'
      },
      {
        id: 'complete',
        title: 'Workflow Complete',
        description: 'Client setup finished successfully',
        status: 'pending'
      }
    ];

    setWorkflowSteps(steps);

    // Step 1: Validate
    await new Promise(resolve => setTimeout(resolve, 500));
    steps[0].status = 'completed';
    steps[0].result = 'Email format valid';
    steps[1].status = 'active';
    setWorkflowSteps([...steps]);

    // Step 2: Generate slug
    await new Promise(resolve => setTimeout(resolve, 300));
    const slug = generateSlug(clientName);
    steps[1].status = 'completed';
    steps[1].result = `URL: /files/${slug}`;
    steps[2].status = 'active';
    setWorkflowSteps([...steps]);

    // Step 3: Create client
    await new Promise(resolve => setTimeout(resolve, 400));
    const newClient: TaxClient = {
      id: Math.random().toString(36).substr(2, 9),
      name: clientName,
      email: clientEmail,
      phone: clientPhone || undefined,
      slug,
      createdAt: new Date(),
      status: 'pending',
      uploadCount: 0,
      totalSize: 0,
      files: [],
      lastActivity: new Date(),
      notes: clientNotes || undefined
    };

    setClients(prev => [...prev, newClient]);
    steps[2].status = 'completed';
    steps[2].result = `Client ID: ${newClient.id}`;
    steps[3].status = 'active';
    setWorkflowSteps([...steps]);

    // Step 4: Send email
    await new Promise(resolve => setTimeout(resolve, 600));

    const uploadLink = `${window.location.origin}/files/${slug}`;
    const template = emailTemplates.find(t => t.id === 'initial')!;

    const emailBody = template.body
      .replace(/{clientName}/g, clientName)
      .replace(/{uploadLink}/g, uploadLink);

    const emailSubject = template.subject
      .replace(/{clientName}/g, clientName);

    try {
      const result = await sendEmail({
        to: clientEmail,
        subject: emailSubject,
        body: emailBody,
        uploadLink
      });

      if (result.success) {
        steps[3].status = 'completed';
        steps[3].result = `Email sent successfully (${result.messageId})`;

        // Update client status
        const updatedClients = clients.map(c =>
          c.id === newClient.id
            ? { ...c, status: 'documents_requested' as const, lastActivity: new Date() }
            : c
        );
        setClients(updatedClients);
      } else {
        steps[3].status = 'error';
        steps[3].result = `Email failed: ${result.error}`;
      }
    } catch (error) {
      steps[3].status = 'error';
      steps[3].result = `Email error: ${error}`;
    }

    steps[4].status = 'active';
    setWorkflowSteps([...steps]);

    // Step 5: Complete
    await new Promise(resolve => setTimeout(resolve, 300));
    steps[4].status = 'completed';
    steps[4].result = 'Client ready to upload documents';
    setWorkflowSteps([...steps]);

    setRunningProcess(false);

    // Clear form
    setTimeout(() => {
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setClientNotes('');
      setWorkflowSteps([]);
    }, 3000);
  };

  // Send reminder email
  const sendReminderEmail = async (client: TaxClient) => {
    if (!selectedTemplate) {
      alert('Please select an email template first');
      return;
    }

    const uploadLink = `${window.location.origin}/files/${client.slug}`;

    const emailBody = selectedTemplate.body
      .replace(/{clientName}/g, client.name)
      .replace(/{uploadLink}/g, uploadLink)
      .replace(/{status}/g, client.status)
      .replace(/{documentCount}/g, client.uploadCount.toString());

    const emailSubject = selectedTemplate.subject
      .replace(/{clientName}/g, client.name);

    try {
      const result = await sendEmail({
        to: client.email,
        subject: emailSubject,
        body: emailBody,
        uploadLink: selectedTemplate.category === 'initial_request' ? uploadLink : undefined
      });

      if (result.success) {
        alert(`Email sent successfully to ${client.name}!`);

        // Update client last activity
        const updatedClients = clients.map(c =>
          c.id === client.id
            ? { ...c, lastActivity: new Date() }
            : c
        );
        setClients(updatedClients);
      } else {
        alert(`Failed to send email: ${result.error}`);
      }
    } catch (error) {
      alert(`Email error: ${error}`);
    }
  };

  // Generate email preview
  useEffect(() => {
    if (selectedTemplate && selectedClient) {
      const uploadLink = `${window.location.origin}/files/${selectedClient.slug}`;

      const preview = selectedTemplate.body
        .replace(/{clientName}/g, selectedClient.name)
        .replace(/{uploadLink}/g, uploadLink)
        .replace(/{status}/g, selectedClient.status)
        .replace(/{documentCount}/g, selectedClient.uploadCount.toString());

      setEmailPreview(preview);
    } else {
      setEmailPreview('');
    }
  }, [selectedTemplate, selectedClient]);

  // Get status color
  const getStatusColor = (status: TaxClient['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'documents_requested': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'documents_received': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'in_progress': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'completed': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // Dashboard stats
  const stats = {
    totalClients: clients.length,
    pendingDocs: clients.filter(c => c.status === 'documents_requested').length,
    inProgress: clients.filter(c => c.status === 'in_progress').length,
    completed: clients.filter(c => c.status === 'completed').length,
    totalDocuments: clients.reduce((sum, c) => sum + c.uploadCount, 0)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            🧾 Tax Preparer Workflow Designer
          </h1>
          <p className="text-xl text-gray-300">
            Design the perfect client onboarding and document collection flow
          </p>
        </motion.div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('new-client')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'new-client'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            👤 New Client
          </button>
          <button
            onClick={() => setActiveTab('client-management')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'client-management'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            📊 Client Management
          </button>
          <button
            onClick={() => setActiveTab('email-templates')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'email-templates'
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            📧 Email Templates
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              activeTab === 'dashboard'
                ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            📈 Dashboard
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* New Client Tab */}
            {activeTab === 'new-client' && (
              <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6">
                <h2 className="text-2xl font-bold mb-6">👤 New Client Onboarding</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Client Name *</label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="John & Jane Smith"
                      className="w-full px-4 py-3 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="john.smith@email.com"
                      className="w-full px-4 py-3 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="(555) 123-4567"
                      className="w-full px-4 py-3 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Initial Notes</label>
                    <input
                      type="text"
                      value={clientNotes}
                      onChange={(e) => setClientNotes(e.target.value)}
                      placeholder="First-time client, owns rental property..."
                      className="w-full px-4 py-3 bg-gray-900 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                {clientName && (
                  <div className="bg-gray-900 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold mb-2">🔗 Generated Upload URL:</h3>
                    <code className="text-blue-400 text-sm">
                      {window.location.origin}/files/{generateSlug(clientName)}
                    </code>
                  </div>
                )}

                <button
                  onClick={runCreateClientWorkflow}
                  disabled={runningProcess || !clientName || !clientEmail}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold text-lg disabled:opacity-50 transition"
                >
                  {runningProcess ? '🚀 Setting up client...' : '🚀 Create Client & Send Email'}
                </button>
              </div>
            )}

            {/* Client Management Tab */}
            {activeTab === 'client-management' && (
              <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6">
                <h2 className="text-2xl font-bold mb-6">📊 Client Management</h2>

                {clients.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">👥</div>
                    <p className="text-gray-400 text-lg">No clients yet</p>
                    <p className="text-gray-500">Create your first client to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {clients.map(client => (
                      <motion.div
                        key={client.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-gray-900 rounded-lg p-4 border-l-4 ${
                          client.status === 'completed' ? 'border-green-500' :
                          client.status === 'in_progress' ? 'border-purple-500' :
                          client.status === 'documents_received' ? 'border-blue-500' :
                          'border-yellow-500'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-semibold">{client.name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(client.status)}`}>
                                {client.status.replace('_', ' ').toUpperCase()}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-400">
                              <div>
                                <span className="block font-medium">Email:</span>
                                {client.email}
                              </div>
                              <div>
                                <span className="block font-medium">Documents:</span>
                                {client.uploadCount} files ({(client.totalSize / 1024 / 1024).toFixed(2)} MB)
                              </div>
                              <div>
                                <span className="block font-medium">Last Activity:</span>
                                {client.lastActivity.toLocaleDateString()}
                              </div>
                            </div>
                            {client.notes && (
                              <div className="mt-2 text-sm text-gray-300">
                                <span className="font-medium">Notes:</span> {client.notes}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/files/${client.slug}`);
                                alert('Upload URL copied!');
                              }}
                              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-semibold transition"
                              title="Copy Upload URL"
                            >
                              📋
                            </button>
                            <button
                              onClick={() => setSelectedClient(client)}
                              className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-semibold transition"
                              title="Send Email"
                            >
                              📧
                            </button>
                            <button
                              onClick={() => {
                                const newStatus = prompt(
                                  'Enter new status (pending, documents_requested, documents_received, in_progress, completed):',
                                  client.status
                                );
                                if (newStatus && ['pending', 'documents_requested', 'documents_received', 'in_progress', 'completed'].includes(newStatus)) {
                                  const updatedClients = clients.map(c =>
                                    c.id === client.id
                                      ? { ...c, status: newStatus as TaxClient['status'], lastActivity: new Date() }
                                      : c
                                  );
                                  setClients(updatedClients);
                                }
                              }}
                              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-semibold transition"
                              title="Update Status"
                            >
                              ⚡
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Email Templates Tab */}
            {activeTab === 'email-templates' && (
              <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6">
                <h2 className="text-2xl font-bold mb-6">📧 Email Templates</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Available Templates</h3>
                    <div className="space-y-2">
                      {emailTemplates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => setSelectedTemplate(template)}
                          className={`w-full text-left p-4 rounded-lg border transition ${
                            selectedTemplate?.id === template.id
                              ? 'bg-blue-600 border-blue-400'
                              : 'bg-gray-900 border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <div className="font-semibold">{template.name}</div>
                          <div className="text-sm text-gray-400 capitalize">
                            {template.category.replace('_', ' ')}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Template Preview</h3>
                    {selectedTemplate ? (
                      <div className="bg-gray-900 rounded-lg p-4 space-y-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Subject:</label>
                          <div className="bg-gray-800 rounded p-2 text-sm">
                            {selectedTemplate.subject}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Body:</label>
                          <div className="bg-gray-800 rounded p-3 text-sm max-h-64 overflow-y-auto whitespace-pre-wrap">
                            {selectedTemplate.body}
                          </div>
                        </div>

                        {selectedClient && (
                          <div className="border-t border-gray-700 pt-4">
                            <h4 className="font-semibold mb-2">Live Preview for {selectedClient.name}:</h4>
                            <div className="bg-gray-800 rounded p-3 text-sm max-h-32 overflow-y-auto whitespace-pre-wrap">
                              {emailPreview}
                            </div>
                            <button
                              onClick={() => sendReminderEmail(selectedClient)}
                              className="mt-3 w-full py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition"
                            >
                              📤 Send Email to {selectedClient.name}
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="bg-gray-900 rounded-lg p-8 text-center text-gray-500">
                        Select a template to preview
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-400">{stats.totalClients}</div>
                    <div className="text-sm text-gray-400">Total Clients</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-400">{stats.pendingDocs}</div>
                    <div className="text-sm text-gray-400">Pending Docs</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-400">{stats.inProgress}</div>
                    <div className="text-sm text-gray-400">In Progress</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-400">{stats.completed}</div>
                    <div className="text-sm text-gray-400">Completed</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-400">{stats.totalDocuments}</div>
                    <div className="text-sm text-gray-400">Total Documents</div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-gray-800/50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">📊 Client Status Overview</h3>
                  {clients.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No clients to display</p>
                  ) : (
                    <div className="space-y-3">
                      {clients.slice().sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime()).slice(0, 10).map(client => (
                        <div key={client.id} className="flex items-center justify-between bg-gray-900 rounded-lg p-3">
                          <div className="flex-1">
                            <div className="font-semibold">{client.name}</div>
                            <div className="text-sm text-gray-400">
                              {client.uploadCount} documents • Last activity: {client.lastActivity.toLocaleDateString()}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(client.status)}`}>
                            {client.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Workflow Steps */}
            {workflowSteps.length > 0 && (
              <div className="bg-gray-800/50 backdrop-blur rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4">🔄 Workflow Progress</h3>
                <div className="space-y-3">
                  {workflowSteps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                        step.status === 'active' ? 'border-blue-500 bg-blue-500/10' :
                        step.status === 'completed' ? 'border-green-500 bg-green-500/10' :
                        step.status === 'error' ? 'border-red-500 bg-red-500/10' :
                        'border-gray-700 bg-gray-800/50'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        step.status === 'completed' ? 'bg-green-500 text-white' :
                        step.status === 'active' ? 'bg-blue-500 text-white animate-pulse' :
                        step.status === 'error' ? 'bg-red-500 text-white' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {step.status === 'completed' ? '✓' :
                         step.status === 'error' ? '✗' :
                         index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{step.title}</div>
                        <div className="text-sm text-gray-400">{step.description}</div>
                        {step.result && (
                          <div className="text-sm text-blue-400 mt-1">→ {step.result}</div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            {/* Quick Actions */}
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-4">
              <h3 className="text-lg font-bold mb-3">⚡ Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setActiveTab('new-client')}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition text-sm"
                >
                  👤 Add New Client
                </button>
                <button
                  onClick={() => {
                    const csv = [
                      ['Name', 'Email', 'Status', 'Documents', 'Last Activity'],
                      ...clients.map(c => [
                        c.name,
                        c.email,
                        c.status,
                        c.uploadCount,
                        c.lastActivity.toISOString()
                      ])
                    ].map(row => row.join(',')).join('\n');

                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'tax-clients.csv';
                    a.click();
                  }}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition text-sm"
                >
                  📥 Export Clients CSV
                </button>
                <button
                  onClick={() => {
                    if (confirm('Clear all client data? This cannot be undone.')) {
                      setClients([]);
                      setSelectedClient(null);
                      localStorage.removeItem('tax_clients');
                    }
                  }}
                  className="w-full py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition text-sm"
                >
                  🗑️ Clear All Data
                </button>
              </div>
            </div>

            {/* Recent Clients */}
            {clients.length > 0 && (
              <div className="bg-gray-800/50 backdrop-blur rounded-xl p-4">
                <h3 className="text-lg font-bold mb-3">👥 Recent Clients</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {clients.slice().sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, 5).map(client => (
                    <div
                      key={client.id}
                      className={`bg-gray-900 rounded-lg p-3 cursor-pointer transition ${
                        selectedClient?.id === client.id ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onClick={() => setSelectedClient(client)}
                    >
                      <div className="font-medium text-sm">{client.name}</div>
                      <div className="text-xs text-gray-400">{client.email}</div>
                      <div className={`text-xs mt-1 px-2 py-1 rounded ${getStatusColor(client.status)}`}>
                        {client.status.replace('_', ' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email System Status */}
            <div className="bg-gray-800/50 backdrop-blur rounded-xl p-4">
              <h3 className="text-lg font-bold mb-3">📧 Email System</h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Status:</span>
                  <span className="text-green-400 font-semibold">🟢 Active</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Provider:</span>
                  <span className="text-blue-400 font-semibold">AWS SES</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Architecture:</span>
                  <span className="text-purple-400 font-semibold">Lambda + Worker</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Secure email delivery via CloudFlare → Lambda → SES
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxPreparerWorkflowDesigner;